import { useEffect, useState } from "react";
import { REST_schema } from "../@types/sharedSchema";
import useWebotServer from "./useWebotServer";
import useFirestore from "./useFirestore";

/**
 * Org-mates' activity (shared notes / meetings / tasks) for a single listing,
 * served by POST /api/v1/listing-org-activity/query.
 *
 * Every card on screen calls this hook with its own listing id; a module-level
 * batcher coalesces all ids requested within a short window into ONE request,
 * so a board page of 50 rows costs a single round trip. Results are cached
 * (with a TTL) across mounts, pages and surfaces — the listings board, the
 * listings-match page, the contact matches tab and the listing dialog all
 * share the same cache.
 *
 * Returns { activity: null } while loading, when the user is not part of an
 * organization, and when no org-mate has activity on the listing.
 */

type Fetcher = (listingIds: string[]) => Promise<REST_schema.ListingOrgActivityResponse>;
type CacheEntry = { fetchedAt: number; activity: REST_schema.ListingOrgActivity | null };

const CACHE_TTL_MS = 2 * 60 * 1000;
// Short-lived negative cache after a failed request, so a broken endpoint
// doesn't get hammered once per card.
const FAILURE_RETRY_MS = 15 * 1000;
const BATCH_WINDOW_MS = 250;
// Server caps at 300; stay under it per request.
const MAX_IDS_PER_REQUEST = 300;

const cache = new Map<string, CacheEntry>();
const pendingIds = new Set<string>();
const inFlightIds = new Set<string>();
const listenersByListingId = new Map<string, Set<() => void>>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let latestFetcher: Fetcher | null = null;

const notifyListeners = (ids: string[]) => {
  ids.forEach((id) => listenersByListingId.get(id)?.forEach((listener) => listener()));
};

const flushPendingBatch = async () => {
  flushTimer = null;
  const fetcher = latestFetcher;
  const ids = Array.from(pendingIds).slice(0, MAX_IDS_PER_REQUEST);
  if (!fetcher || ids.length === 0) return;
  ids.forEach((id) => {
    pendingIds.delete(id);
    inFlightIds.add(id);
  });
  try {
    const response = await fetcher(ids);
    const now = Date.now();
    ids.forEach((id) =>
      cache.set(id, { fetchedAt: now, activity: response.activityByListingId[id] ?? null })
    );
  } catch (error) {
    console.error("Failed to fetch listing org activity", error);
    const retryAt = Date.now() - CACHE_TTL_MS + FAILURE_RETRY_MS;
    ids.forEach((id) => {
      if (!cache.has(id)) cache.set(id, { fetchedAt: retryAt, activity: null });
    });
  } finally {
    ids.forEach((id) => inFlightIds.delete(id));
    notifyListeners(ids);
    if (pendingIds.size > 0 && !flushTimer) flushTimer = setTimeout(flushPendingBatch, BATCH_WINDOW_MS);
  }
};

const requestListingActivity = (listingId: string) => {
  const entry = cache.get(listingId);
  if (entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS) return;
  if (inFlightIds.has(listingId) || pendingIds.has(listingId)) return;
  pendingIds.add(listingId);
  if (!flushTimer) flushTimer = setTimeout(flushPendingBatch, BATCH_WINDOW_MS);
};

export default function useListingOrgActivity(listingId?: string): {
  activity: REST_schema.ListingOrgActivity | null;
  isLoading: boolean;
} {
  const { getListingOrgActivity } = useWebotServer();
  const { organizationId } = useFirestore();
  const [, bumpVersion] = useState(0);

  useEffect(() => {
    if (!listingId || !organizationId) return undefined;
    latestFetcher = getListingOrgActivity;

    const listener = () => bumpVersion((n) => n + 1);
    let listeners = listenersByListingId.get(listingId);
    if (!listeners) {
      listeners = new Set();
      listenersByListingId.set(listingId, listeners);
    }
    listeners.add(listener);
    requestListingActivity(listingId);

    return () => {
      listeners!.delete(listener);
      if (listeners!.size === 0) listenersByListingId.delete(listingId);
    };
  }, [listingId, organizationId, getListingOrgActivity]);

  if (!listingId || !organizationId) return { activity: null, isLoading: false };
  const entry = cache.get(listingId);
  return { activity: entry?.activity ?? null, isLoading: !entry };
}
